import React, { useState, useEffect } from 'react';
import AppLoadingState from './common/AppLoadingState';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { AppPageShell, PageHeader } from './common/ui';

const BlogAdmin = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    readTime: '',
    category: '',
    imageUrl: '',
    gradient: '',
    published: false
  });

  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const posts = await api.getBlogPosts();
      setPosts(posts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch blog posts');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'category') {
      const categoryArray = value.split(',').map(cat => cat.trim());
      setCategories(categoryArray.filter(Boolean));
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      setUploadingImage(true);
      const response = await api.uploadBlogImage(file);
      setFormData(prev => ({
        ...prev,
        imageUrl: response.url
      }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      if (value && !categories.includes(value)) {
        const newCategories = [...categories, value];
        setCategories(newCategories);
        setFormData(prev => ({
          ...prev,
          category: newCategories.join(', ')
        }));
        e.target.value = '';
      }
    }
  };

  const removeCategory = (categoryToRemove) => {
    const newCategories = categories.filter(cat => cat !== categoryToRemove);
    setCategories(newCategories);
    setFormData(prev => ({
      ...prev,
      category: newCategories.join(', ')
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      readTime: '',
      category: '',
      imageUrl: '',
      gradient: '',
      published: false
    });
    setCategories([]);
    setEditingPost(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        read_time: formData.readTime,
        image_url: formData.imageUrl,
        category: categories.join(', ')
      };

      if (editingPost) {
        await api.updateBlogPost(editingPost.id, data);
        toast.success('Blog post updated successfully');
      } else {
        await api.createBlogPost(data);
        toast.success('Blog post created successfully');
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.message || 'Failed to save blog post');
    }
  };

  const handleEdit = (post) => {
    const postCategories = post.category.split(',').map(cat => cat.trim()).filter(Boolean);
    setCategories(postCategories);
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      readTime: post.read_time,
      category: post.category,
      imageUrl: post.image_url,
      gradient: post.gradient,
      published: post.published
    });
    setImagePreview(post.image_url);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await api.deleteBlogPost(id);
      toast.success('Blog post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete blog post');
    }
  };

  if (loading) {
    return <AppLoadingState layout="page" showLabel={false} />;
  }

  return (
    <AppPageShell maxWidthClass="max-w-7xl">
      <PageHeader
        eyebrow="Admin"
        title={editingPost ? 'Edit blog post' : 'Create blog post'}
        description="Author marketing posts with slug, excerpt, hero image, and rich content for the public blog."
      />
      <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-6 sm:p-8 mb-8 shadow-[var(--app-panel-shadow)]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="app-field w-full rounded-lg px-4 py-2"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
                Slug
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                className="app-field w-full rounded-lg px-4 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
                Categories
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  name="category"
                  placeholder="Add categories (press Enter)"
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="app-field w-full rounded-lg px-4 py-2"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {categories.map((category, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm bg-blue-500/20 text-blue-400"
                    >
                      {category}
                      <button
                        type="button"
                        onClick={() => removeCategory(category)}
                        className="ml-1 hover:text-blue-300 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
                Read Time (minutes)
              </label>
              <input
                type="number"
                name="readTime"
                value={formData.readTime}
                onChange={handleInputChange}
                className="app-field w-full rounded-lg px-4 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
                Featured Image
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="app-field w-full rounded-lg px-4 py-2"
                />
                {uploadingImage && (
                  <div className="text-sm text-blue-400">Uploading image...</div>
                )}
                {imagePreview && (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
                Gradient
              </label>
              <input
                type="text"
                name="gradient"
                value={formData.gradient}
                onChange={handleInputChange}
                className="app-field w-full rounded-lg px-4 py-2"
                required
                placeholder="from-[color] to-[color]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
              Excerpt
            </label>
            <textarea
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
              rows="3"
              className="app-field w-full rounded-lg px-4 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--app-muted)] mb-2">
              Content (Markdown)
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              rows="20"
              className="app-field w-full rounded-lg px-4 py-2 font-mono"
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="published"
              checked={formData.published}
              onChange={handleInputChange}
              className="h-4 w-4 rounded border-[var(--app-field-border)] text-[var(--app-accent)] focus:ring-[var(--app-accent-ring)]"
            />
            <label className="ml-2 text-sm text-[var(--app-muted)]">
              Published
            </label>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={uploadingImage}
              className={`px-6 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium transition-colors ${
                uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {editingPost ? 'Update Post' : 'Create Post'}
            </button>
            {editingPost && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white font-medium transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-8 shadow-[var(--app-panel-shadow)]">
        <h2 className="text-xl font-bold text-[var(--app-ink)] mb-6">Blog posts</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map(post => (
            <div
              key={post.id}
              className="rounded-xl border border-[var(--app-border)] bg-[var(--app-readonly-bg)] p-6 space-y-4"
            >
              <div className="aspect-video rounded-lg overflow-hidden mb-4">
                <img 
                  src={post.image_url} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-semibold text-[var(--app-ink)]">{post.title}</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {post.category.split(',').map((category, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm bg-blue-500/20 text-blue-400"
                  >
                    {category.trim()}
                  </span>
                ))}
              </div>
              <p className="text-[var(--app-muted)] line-clamp-2">{post.excerpt}</p>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  post.published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {post.published ? 'Published' : 'Draft'}
                </span>
                <span className="text-[var(--app-muted)] text-sm">{new Date(post.date).toLocaleDateString()}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(post)}
                  className="px-4 py-1 rounded-md border border-[var(--app-border)] bg-[var(--app-control-bg)] text-[var(--app-ink)] text-sm transition-colors hover:bg-[var(--app-control-bg-hover)]"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="px-4 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-red-400 text-sm transition-colors"
                >
                  Delete
                </button>
                {post.published && (
                  <button
                    onClick={() => navigate(`/blog/${post.slug}`)}
                    className="px-4 py-1 bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-400 text-sm transition-colors"
                  >
                    View
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppPageShell>
  );
};

export default BlogAdmin; 